// Unified AuthProvider.js
import { createContext, useContext, useEffect, useState } from "react";
import { auth, db } from "../utils/firebase";
import { onAuthStateChanged, onIdTokenChanged } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { ADMIN_USER_IDS, ADMIN_EMAILS } from "../utils/adminUtils";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

export function AuthProvider({ children }) {
  const [ready, setReady] = useState(false);
  const [user, setUser] = useState(null);
  const [claims, setClaims] = useState(null);
  const [entitlements, setEntitlements] = useState(null);
  const [role, setRole] = useState("user");
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSubscriber, setIsSubscriber] = useState(false);
  const [loading, setLoading] = useState(true);

  // Unified auth state management
  useEffect(() => {
    let isMounted = true;

    const unsubAuth = onAuthStateChanged(auth, async (u) => {
      if (!isMounted) return;
      
      setUser(u);
      setLoading(true);

      if (!u) {
        // User logged out
        setClaims(null);
        setEntitlements(null);
        setRole("user");
        setIsAdmin(false);
        setIsSubscriber(false);
        setReady(true);
        setLoading(false);
        return;
      }

      try {
        // Get claims from ID token
        const tokenResult = await u.getIdTokenResult();
        const newClaims = tokenResult.claims || {};
        setClaims(newClaims);

        // Load user document from Firestore (if user has access)
        let userData = null;
        let newRole = newClaims?.role || "user";
        try {
          const userDoc = await getDoc(doc(db, "users", u.uid));
          if (userDoc.exists()) {
            userData = userDoc.data();
            // Use Firestore role if available, otherwise use claims role
            if (userData.role) {
              newRole = userData.role;
            }
            const userEntitlements = userData.entitlements || null;
            setEntitlements(userEntitlements);
            
            // Determine subscription status
            const planActive = !!userEntitlements?.planActive;
            setIsSubscriber(planActive);
          } else {
            setEntitlements(null);
            setIsSubscriber(false);
          }
        } catch (error) {
          // Firestore rules may block access - that's fine
          console.log("Could not load user document:", error.message);
          setEntitlements(null);
          setIsSubscriber(false);
        }

        setRole(newRole);

        // Determine admin status - comprehensive check
        // Check: 1) Token claims, 2) ADMIN_USER_IDS, 3) ADMIN_EMAILS, 4) Firestore role
        const isAdminByClaims = newClaims?.admin === true || newRole === "admin";
        const isAdminByUid = ADMIN_USER_IDS.includes(u.uid);
        const isAdminByEmail = u.email && ADMIN_EMAILS.includes(u.email);
        const isAdminByFirestore = userData?.role === "admin";
        
        const newIsAdmin = isAdminByClaims || isAdminByUid || isAdminByEmail || isAdminByFirestore;
        setIsAdmin(newIsAdmin);

        // Log admin check for debugging
        if (newIsAdmin) {
          console.log("✅ Admin access granted:", {
            uid: u.uid,
            email: u.email,
            checks: {
              claims: isAdminByClaims,
              uid: isAdminByUid,
              email: isAdminByEmail,
              firestore: isAdminByFirestore
            }
          });
        }

        setReady(true);
        setLoading(false);
      } catch (error) {
        console.error("Error loading user data:", error);
        if (isMounted) {
          setReady(true);
          setLoading(false);
        }
      }
    });

    // Listen for token changes to update claims
    const unsubToken = onIdTokenChanged(auth, async (u) => {
      if (!isMounted || !u) return;
      
      try {
        const tokenResult = await u.getIdTokenResult();
        const newClaims = tokenResult.claims || {};
        setClaims(newClaims);
        
        // Get user document to check Firestore role
        let userData = null;
        let newRole = newClaims?.role || "user";
        try {
          const userDoc = await getDoc(doc(db, "users", u.uid));
          if (userDoc.exists()) {
            userData = userDoc.data();
            if (userData.role) {
              newRole = userData.role;
            }
          }
        } catch (error) {
          // Firestore rules may block access - that's fine
        }
        
        setRole(newRole);
        
        // Update admin status with comprehensive check
        const isAdminByClaims = newClaims?.admin === true || newRole === "admin";
        const isAdminByUid = ADMIN_USER_IDS.includes(u.uid);
        const isAdminByEmail = u.email && ADMIN_EMAILS.includes(u.email);
        const isAdminByFirestore = userData?.role === "admin";
        
        setIsAdmin(isAdminByClaims || isAdminByUid || isAdminByEmail || isAdminByFirestore);
      } catch (error) {
        console.error("Error updating claims:", error);
      }
    });

    return () => {
      isMounted = false;
      unsubAuth();
      unsubToken();
    };
  }, []);

  const value = {
    // Core auth state
    ready,
    user,
    claims,
    loading,
    
    // User data
    entitlements,
    role,
    isAdmin,
    isSubscriber,
    
    // Computed properties
    uid: user?.uid || null,
    email: user?.email || null,
    displayName: user?.displayName || null,
    emailVerified: user?.emailVerified || false,
  };

  return (
    <AuthCtx.Provider value={value}>
      {children}
    </AuthCtx.Provider>
  );
}
